import { Button, Icon, InputField } from '@design-system';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFlow } from '../../../providers/FlowProvider';
import { usePreAuthDrawCtx } from '../../../providers/PreAuthDrawProvider';
import { DrawByProductId } from '../../draws/types';

interface Props {
  draw: DrawByProductId;
  product: ProductType;
}

export const BidStep = ({ draw }: Props) => {
  const { t } = useTranslation('product');
  const { goToStep } = useFlow();
  const { entryIntentResponse, setBid } = usePreAuthDrawCtx();

  const form = useForm({
    defaultValues: {
      bid: entryIntentResponse.intent?.bid?.amount
        ? Number(entryIntentResponse.intent.bid.amount)
        : '',
    },
  });

  const getSafeNumber = (value: string | number): number => {
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const isValidBid =
    draw.auctionReservePrice &&
    getSafeNumber(form.watch('bid')) >= Number(draw.auctionReservePrice.amount);

  const handleSubmit = ({ bid }: { bid: number }) => {
    setBid(bid);
    goToStep('frame-intro');
  };

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-6"
        onSubmit={(ev) => void form.handleSubmit(handleSubmit)(ev)}
      >
        <InputField
          key="bid"
          {...form.register('bid', {
            required: {
              message: t('drawers.bid.input.required'),
              value: true,
            },
            min: {
              message: t('drawers.bid.input.min'),
              value: draw.auctionReservePrice?.amount ?? 0,
            },
          })}
          error={form.formState.errors.bid?.message}
          id="bid"
          note={t('drawers.bid.input.note', {
            amount: draw.auctionReservePrice?.amount ?? 0,
          })}
          prefix={<span className="font-medium text-sm">€</span>}
        />
        <Button
          disabled={!isValidBid}
          fullWidth
          label={t('drawers.bid.save')}
          // loading={loading}
          type="submit"
          variant="primary"
        />
      </form>
      <div className="flex flex-col gap-4">
        <h3 className="font-bold">{t('drawers.bid.help.how_it_works')}</h3>
        <div className="flex gap-3">
          <div>
            <Icon name="ic_ticket" size="sm" />
          </div>
          <div className="pt-0.5">
            <h4>{t('drawers.bid.help.place_your_bid.title')}</h4>
            <p className="text-neutral-4">
              {t('drawers.bid.help.place_your_bid.description', {
                allocation: draw.allocation,
              })}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <Icon name="ic_receipt" size="sm" />
          </div>
          <div className="pt-0.5">
            <h4>{t('drawers.bid.help.final_price.title')}</h4>
            <p className="text-neutral-4">
              {t('drawers.bid.help.final_price.description', {
                allocation: draw.allocation,
              })}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <Icon name="ic_check_circle" size="sm" />
          </div>
          <div className="pt-0.5">
            <h4>
              {t('drawers.bid.help.top_bidders.title', {
                allocation: draw.allocation,
              })}
            </h4>
            <p className="text-neutral-4">
              {t('drawers.bid.help.top_bidders.description')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
